import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  useTheme,
  Button,
  CircularProgress,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Card,
  CardContent,
  Chip,
  Divider,
  Grid,
  Avatar,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Snackbar,
  Alert,
  TextField,
  InputAdornment,
} from "@mui/material";
import {
  ArrowBackIosNewOutlined,
  ExpandMoreOutlined,
  PersonOutlined,
  CalendarTodayOutlined,
  InfoOutlined,
  VisibilityOutlined,
  SearchOutlined,
  FilterListOutlined,
} from "@mui/icons-material";
import { tokens } from "../../theme";
import { getToken, base_url } from "../../config/config";
import axios from "axios";
import { useNavigate } from "react-router-dom";

const ProductLogs = () => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const navigate = useNavigate();

  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchText, setSearchText] = useState("");
  const [selectedLog, setSelectedLog] = useState(null);
  const [detailsDialogOpen, setDetailsDialogOpen] = useState(false);
  const [alert, setAlert] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  useEffect(() => {
    fetchLogs();
  }, []);

  const fetchLogs = async () => {
    setLoading(true);
    try {
      const token = getToken();
      const response = await axios.get(`${base_url}/v1/stock/get-stock-logs`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (response.data.status === 1) {
        // Process the new data structure
        const processedLogs = processGroupedLogs(response.data.data);
        setLogs(processedLogs);
      }
    } catch (error) {
      console.error("Error fetching logs:", error);
      setAlert({
        open: true,
        message: "Fehler beim Laden der Produkt-Logs",
        severity: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  const processGroupedLogs = (groupedData) => {
    return groupedData
      .map((group) => {
        // Sort logs by creation date (newest first)
        const sortedLogs = group.logs.sort(
          (a, b) => new Date(b.createdAt) - new Date(a.createdAt)
        );

        // Check if productId exists as an object (current product) or if we need to extract from logDetails
        if (group.productId && typeof group.productId === "object") {
          // Active product with full data
          return {
            productId: group.productId._id,
            productName: group.productId.name,
            artikelNummer: group.productId.artikelNummer,
            productData: group.productId,
            logs: sortedLogs,
            lastActivity: sortedLogs[0]?.createdAt,
            hasProduct: true,
          };
        } else if (group._id && group.logs.length > 0) {
          // Try to extract product info from logDetails
          let productName = "Unbekanntes Produkt";
          let artikelNummer = "N/A";

          try {
            const firstLog = group.logs[0];
            if (firstLog.logDetails) {
              const details = JSON.parse(firstLog.logDetails);
              productName = details.name || "Gelöschtes Produkt";
              artikelNummer = details.artikelNummer || "N/A";
            }
          } catch (e) {
            // Keep default values if parsing fails
          }

          return {
            productId: group._id,
            productName,
            artikelNummer,
            productData: null,
            logs: sortedLogs,
            lastActivity: sortedLogs[0]?.createdAt,
            hasProduct: false, // This is a deleted/orphaned product
          };
        }

        return null; // Skip invalid entries
      })
      .filter(Boolean) // Remove null entries
      .sort((a, b) => new Date(b.lastActivity) - new Date(a.lastActivity));
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString("de-DE", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const getLogTypeColor = (logType) => {
    switch (logType) {
      case "created":
        return "success";
      case "updated":
        return "info";
      case "deleted":
        return "error";
      case "moved":
        return "warning";
      default:
        return "default";
    }
  };

  const getLogTypeText = (logType) => {
    switch (logType) {
      case "created":
        return "Erstellt";
      case "updated":
        return "Aktualisiert";
      case "deleted":
        return "Gelöscht";
      case "moved":
        return "Verschoben";
      default:
        return logType;
    }
  };

  const handleViewDetails = (log) => {
    setSelectedLog(log);
    setDetailsDialogOpen(true);
  };

  const filteredLogs = logs.filter(
    (group) =>
      group.productName.toLowerCase().includes(searchText.toLowerCase()) ||
      group.artikelNummer.toLowerCase().includes(searchText.toLowerCase())
  );

  // Calculate statistics
  const totalProducts = logs.filter((group) => group.hasProduct).length;
  const totalLogs = logs.reduce((total, group) => total + group.logs.length, 0);
  const todayLogs = logs.filter((group) =>
    group.logs.some(
      (log) =>
        new Date(log.createdAt).toDateString() === new Date().toDateString()
    )
  ).length;
  const activeProducts = logs.filter((group) => group.hasProduct).length;

  if (loading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        height="80vh"
      >
        <CircularProgress size={60} />
      </Box>
    );
  }

  return (
    <Box m="20px">
      {/* HEADER */}
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        mb={3}
      >
        <Box>
          <Typography
            variant="h2"
            color={colors.grey[100]}
            fontWeight="bold"
            mb="5px"
          >
            Produkt-Logs
          </Typography>
          <Typography variant="h5" color={colors.greenAccent[400]}>
            Aktivitätsverlauf aller Produkte verfolgen
          </Typography>
        </Box>

        <Button
          variant="outlined"
          color="secondary"
          startIcon={<ArrowBackIosNewOutlined />}
          onClick={() => navigate("/stock/products")}
        >
          Zurück zu Produkten
        </Button>
      </Box>

      {/* SEARCH BAR */}
      <TextField
        fullWidth
        variant="filled"
        label="Produkte durchsuchen"
        placeholder="Nach Produktname oder Artikelnummer suchen..."
        value={searchText}
        onChange={(e) => setSearchText(e.target.value)}
        sx={{ mb: 3 }}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchOutlined />
            </InputAdornment>
          ),
        }}
      />

      {/* STATS CARDS */}
      <Grid container spacing={2} mb={3}>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ bgcolor: colors.primary[400] }}>
            <CardContent>
              <Typography variant="h6" color={colors.grey[300]}>
                Aktive Produkte
              </Typography>
              <Typography variant="h4" color={colors.greenAccent[400]}>
                {activeProducts}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ bgcolor: colors.primary[400] }}>
            <CardContent>
              <Typography variant="h6" color={colors.grey[300]}>
                Gesamt Logs
              </Typography>
              <Typography variant="h4" color={colors.blueAccent[400]}>
                {totalLogs}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ bgcolor: colors.primary[400] }}>
            <CardContent>
              <Typography variant="h6" color={colors.grey[300]}>
                Heute Aktivitäten
              </Typography>
              <Typography variant="h4" color={colors.redAccent[400]}>
                {todayLogs}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ bgcolor: colors.primary[400] }}>
            <CardContent>
              <Typography variant="h6" color={colors.grey[300]}>
                Gesamt Gruppen
              </Typography>
              <Typography variant="h4" color={colors.greenAccent[500]}>
                {logs.length}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* LOGS ACCORDION */}
      {filteredLogs.length === 0 ? (
        <Card sx={{ bgcolor: colors.primary[400], textAlign: "center", p: 4 }}>
          <Typography variant="h6" color={colors.grey[300]}>
            Keine Logs gefunden
          </Typography>
          <Typography variant="body2" color={colors.grey[400]}>
            {searchText
              ? "Versuchen Sie einen anderen Suchbegriff"
              : "Es sind noch keine Produkt-Logs vorhanden"}
          </Typography>
        </Card>
      ) : (
        <Box>
          {filteredLogs.map((group, index) => (
            <Accordion
              key={group.productId}
              sx={{
                bgcolor: colors.primary[400],
                mb: 1,
                "&:before": { display: "none" },
                "&.Mui-expanded": {
                  boxShadow: `0 4px 20px 0 ${colors.primary[700]}`,
                },
                ...(group.hasProduct
                  ? {}
                  : {
                      border: `2px dashed ${colors.redAccent[500]}`,
                      opacity: 0.9,
                    }),
              }}
            >
              <AccordionSummary
                expandIcon={
                  <ExpandMoreOutlined sx={{ color: colors.grey[100] }} />
                }
                sx={{
                  bgcolor: group.hasProduct
                    ? colors.primary[500]
                    : colors.redAccent[800],
                  "&.Mui-expanded": {
                    bgcolor: group.hasProduct
                      ? colors.primary[600]
                      : colors.redAccent[700],
                  },
                }}
              >
                <Box
                  display="flex"
                  alignItems="center"
                  justifyContent="space-between"
                  width="100%"
                  mr={2}
                >
                  <Box display="flex" alignItems="center" gap={2}>
                    {group.productData?.image?.s3Url ? (
                      <Avatar
                        src={group.productData.image.s3Url}
                        alt={group.productName}
                        sx={{ width: 40, height: 40, borderRadius: 1 }}
                      />
                    ) : (
                      <Avatar
                        sx={{
                          width: 40,
                          height: 40,
                          borderRadius: 1,
                          bgcolor: group.hasProduct
                            ? colors.blueAccent[700]
                            : colors.redAccent[700],
                          color: colors.grey[100],
                        }}
                      >
                        {group.hasProduct
                          ? group.productName.charAt(0).toUpperCase()
                          : "⚠"}
                      </Avatar>
                    )}
                    <Box>
                      <Typography variant="h6" color={colors.grey[100]}>
                        {group.productName}
                        {!group.hasProduct && (
                          <Chip
                            label="Gelöscht"
                            size="small"
                            color="error"
                            sx={{ ml: 1 }}
                          />
                        )}
                      </Typography>
                      <Typography variant="body2" color={colors.grey[300]}>
                        Artikelnummer: {group.artikelNummer}
                      </Typography>
                    </Box>
                  </Box>
                  <Box display="flex" alignItems="center" gap={2}>
                    <Chip
                      label={`${group.logs.length} Log${
                        group.logs.length !== 1 ? "s" : ""
                      }`}
                      size="small"
                      color="secondary"
                      variant="outlined"
                    />
                    <Typography variant="caption" color={colors.grey[400]}>
                      Letzte Aktivität: {formatDate(group.lastActivity)}
                    </Typography>
                  </Box>
                </Box>
              </AccordionSummary>
              <AccordionDetails sx={{ p: 0 }}>
                <Box>
                  {group.logs.map((log, logIndex) => (
                    <Box
                      key={log._id}
                      sx={{
                        p: 2,
                        borderBottom:
                          logIndex < group.logs.length - 1
                            ? `1px solid ${colors.grey[700]}`
                            : "none",
                        "&:hover": {
                          bgcolor: colors.primary[600],
                        },
                      }}
                    >
                      <Box
                        display="flex"
                        justifyContent="space-between"
                        alignItems="flex-start"
                        mb={1}
                      >
                        <Box display="flex" alignItems="center" gap={1}>
                          <PersonOutlined
                            sx={{ fontSize: 16, color: colors.grey[400] }}
                          />
                          <Typography variant="body2" color={colors.grey[300]}>
                            {log.userName}
                          </Typography>
                          <Chip
                            label={getLogTypeText(log.logType)}
                            size="small"
                            color={getLogTypeColor(log.logType)}
                            sx={{ ml: 1 }}
                          />
                        </Box>
                        <Box display="flex" alignItems="center" gap={1}>
                          <CalendarTodayOutlined
                            sx={{ fontSize: 14, color: colors.grey[400] }}
                          />
                          <Typography
                            variant="caption"
                            color={colors.grey[400]}
                          >
                            {formatDate(log.createdAt)}
                          </Typography>
                          <IconButton
                            size="small"
                            onClick={() => handleViewDetails(log)}
                            sx={{ ml: 1 }}
                          >
                            <VisibilityOutlined sx={{ fontSize: 16 }} />
                          </IconButton>
                        </Box>
                      </Box>
                      <Typography
                        variant="body2"
                        color={colors.grey[200]}
                        sx={{ ml: 3 }}
                      >
                        {log.message}
                      </Typography>
                    </Box>
                  ))}
                </Box>
              </AccordionDetails>
            </Accordion>
          ))}
        </Box>
      )}

      {/* LOG DETAILS DIALOG */}
      <Dialog
        open={detailsDialogOpen}
        onClose={() => setDetailsDialogOpen(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            bgcolor: colors.primary[400],
            color: colors.grey[100],
          },
        }}
      >
        <DialogTitle sx={{ bgcolor: colors.primary[500] }}>
          <Typography variant="h4" component="div" color={colors.grey[100]}>
            Log-Details
          </Typography>
        </DialogTitle>
        <DialogContent sx={{ bgcolor: colors.primary[400] }}>
          {selectedLog && (
            <Box>
              <Grid container spacing={2} mb={3}>
                <Grid item xs={6}>
                  <Typography variant="body2" color={colors.grey[300]}>
                    Benutzer
                  </Typography>
                  <Typography variant="body1" color={colors.grey[100]}>
                    {selectedLog.userName || "System"}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2" color={colors.grey[300]}>
                    Zeitstempel
                  </Typography>
                  <Typography variant="body1" color={colors.grey[100]}>
                    {formatDate(selectedLog.createdAt)}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2" color={colors.grey[300]}>
                    Aktion
                  </Typography>
                  <Chip
                    label={getLogTypeText(selectedLog.logType)}
                    color={getLogTypeColor(selectedLog.logType)}
                    size="small"
                  />
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2" color={colors.grey[300]}>
                    Benutzermodell
                  </Typography>
                  <Typography variant="body1" color={colors.grey[100]}>
                    {selectedLog.userModel || "N/A"}
                  </Typography>
                </Grid>
              </Grid>

              <Divider sx={{ my: 2, borderColor: colors.grey[600] }} />

              <Typography variant="h6" mb={2} color={colors.grey[100]}>
                Nachricht
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  p: 2,
                  bgcolor: colors.primary[500],
                  borderRadius: 1,
                  mb: 2,
                  color: colors.grey[200],
                }}
              >
                {selectedLog.message}
              </Typography>

              {selectedLog.logDetails && (
                <>
                  <Typography variant="h6" mb={2} color={colors.grey[100]}>
                    Detaillierte Informationen
                  </Typography>
                  <Box
                    sx={{
                      p: 2,
                      bgcolor: colors.primary[500],
                      borderRadius: 1,
                      maxHeight: 300,
                      overflow: "auto",
                    }}
                  >
                    <pre
                      style={{
                        whiteSpace: "pre-wrap",
                        fontSize: "12px",
                        color: colors.grey[200],
                        margin: 0,
                      }}
                    >
                      {selectedLog.logDetails}
                    </pre>
                  </Box>
                </>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2, bgcolor: colors.primary[500] }}>
          <Button
            onClick={() => setDetailsDialogOpen(false)}
            color="secondary"
            sx={{ color: colors.grey[100] }}
          >
            Schließen
          </Button>
        </DialogActions>
      </Dialog>

      {/* ALERT SNACKBAR */}
      <Snackbar
        open={alert.open}
        autoHideDuration={6000}
        onClose={() => setAlert({ ...alert, open: false })}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert
          onClose={() => setAlert({ ...alert, open: false })}
          severity={alert.severity}
          sx={{ width: "100%" }}
        >
          {alert.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default ProductLogs;
